import React, { ErrorInfo, ReactNode } from "react";
import ErrorIcon from "./ErrorIcon";
import { StyledAppBoundary } from "./index.styled";
import { TbRefresh } from "react-icons/tb";
import { Button } from "antd";
import { isDev } from "@lib/helpers/General";
import StackTrace from "stacktrace-js";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (import.meta.env.MODE === "development") {
      StackTrace.fromError(error).then((frames) => {
        const filtered = frames
          .filter((f) => f.fileName?.includes("/src/"))
          .map((f) => `${f.functionName} (${f.fileName}:${f.lineNumber})`);

        console.error("Clean stack:", filtered.join("\n"));
      });
    }

    this.setState({ error, errorInfo });
  }

  handleReload = () => {
    window.location.reload();
  };

  cleanStack = (stack?: string) => {
    if (!stack) return "";
    return (
      stack
        .split("\n")
        // .filter((line) => line.includes("/src/"))
        .slice(0, 5)
        .join("\n")
    );
  };

  render() {
    if (this.state.hasError) {
      return (
        <StyledAppBoundary>
          <ErrorIcon />
          <h1 style={{ fontSize: 30, marginTop: 8, color: "#e74c3c" }}>
            Ah! Something went wrong.
          </h1>

          <p style={{ fontSize: 18, textAlign: "center", marginTop: 8 }}>
            Please refresh the page or try again later.
          </p>

          <Button
            type="primary"
            onClick={this.handleReload}
            icon={<TbRefresh />}
          >
            Refresh Page
          </Button>

          {isDev() && this.state.error && (
            <div
              style={{
                marginTop: 24,
                textAlign: "left",
                background:
                  "linear-gradient(to right,rgba(33, 50, 68,0.5),rgba(6, 101, 165, 0.5))",
                color: "#ecf0f1",
                padding: 16,
                borderRadius: 8,
                width: "100%",
                overflowX: "auto",
              }}
            >
              <h3 style={{ marginTop: 0 }}>Error Details (Dev only):</h3>
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {this.cleanStack(this.state?.error?.message)}
              </pre>
              <pre style={{ whiteSpace: "pre-wrap" }}>
                {this.cleanStack(this.state?.errorInfo?.componentStack)}
              </pre>
            </div>
          )}
        </StyledAppBoundary>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
